import { OutpatientRevenuePanel } from "./OutpatientRevenuePanel";
import { ReferralTrendsPanel, ReferralSidebar } from "./ReferralTrendsPanel";
import { SalaryForecastPanel } from "./SalaryForecastPanel";
// src/components/BetaApp.tsx (2026-07-01 新設)
// 開発版(タブ)ページ (/beta)。リリース前タブ (BETA_TABS) だけを載せる。
//   ★2026-08-24: 呼称を「ベータ版」→「開発版」に統一 (/theta の新GP版と認証を共通化した)。
//     URL (/beta) と DB 列 (users.beta) は互換のためそのまま。
// 正式版 App と同じシェル (header + サイドバー grid) を踏襲。アクセスは AuthGate betaOnly で
// beta||admin に限定済 (server も /api/query/profit を requireBeta で保護)。
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { FilterBar } from "./FilterBar";
import { ContextBar } from "./ContextBar";
import { TabNav, tabLabel } from "./TabNav";
import { AuthHeader } from "./AuthHeader";
import { ProfitPanel } from "./ProfitPanel";
import { GpEfficiencyPanel } from "./GpEfficiencyPanel";
import { PrototypePanel } from "./PrototypePanel";
import { MeisaiPanel } from "./MeisaiPanel";
import { DlPanel } from "./DlPanel";
import { SurgeryKaikeiPanel } from "./SurgeryKaikeiPanel";
import { BillingReconPanel } from "./BillingReconPanel";
import { SokuhouUriagePanel } from "./SokuhouUriagePanel";
import { ReferenceSourcesPanel } from "./ReferenceSourcesPanel";
import { DataDictPanel } from "./DataDictPanel";
import { NursingAcuityPanel } from "./NursingAcuityPanel";
import { AiAnalysisPanel } from "./AiAnalysisPanel";
import { InpatientBudgetComparePanel } from "./InpatientBudgetComparePanel";
import { useFilters, BETA_TABS } from "../store";
import { useAuth } from "../lib/useAuth";

export function BetaApp() {
  const navigate = useNavigate();
  const {
    data: meta,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["meta"],
    queryFn: api.meta,
    staleTime: Infinity,
  });
  const f = useFilters();
  const { canViewDoctor } = useAuth();
  const [sbOpen, setSbOpen] = useState(true);
  const isReferral = f.tab === "紹介・入院分析";
  const isNursingAcuity = f.tab === "外来収入" || f.tab === "看護必要度速報" || f.tab === "AI分析" || f.tab === "参照データ一覧" || f.tab === "給与見込み・GP比較";

  // [2026-08-03] 医師名を出すタブは can_view_doctor が無い β ユーザには見せない。
  //   endpoint 側も 403 で守っているが、β の先頭タブがこれなので、権限が無い人が /beta を開くと
  //   いきなりエラー画面になってしまう (β許可 と 主治医閲覧許可 は別の権限)。
  const tabs = canViewDoctor
    ? BETA_TABS
    : BETA_TABS.filter((t) => t !== "入院実績(医師別)");

  // 開発版(タブ)ページに入ったら tab を beta タブに合わせる (FilterBar が粗利用コントロールを出すため)
  useEffect(() => {
    if (f.tab === "AI分析" || f.tab === "紹介・入院分析") {
      navigate(`/${window.location.search}`, { replace: true });
      return;
    }
    if (!tabs.includes(f.tab)) f.setTab(tabs[0]);
  }, [f.tab, canViewDoctor]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.title = `開発版_${tabLabel(f.tab)}_病院実績`;
  }, [f.tab]);

  if (error) {
    return (
      <div className="p-8 max-w-3xl">
        <h1 className="text-xl font-semibold text-red-700 mb-2">起動エラー</h1>
        <pre className="bg-red-50 border border-red-200 rounded p-3 text-xs overflow-auto">
          {(error as Error).message}
        </pre>
      </div>
    );
  }
  if (isLoading || !meta) {
    return (
      <div className="p-8">
        <div className="animate-pulse text-slate-400">読み込み中…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-amber-200 bg-amber-50/60">
        {/* [2026-07-17 ユーザ指示] /beta はページ幅を目一杯使う (max-w 制限なし) */}
        <div className="px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
          <h1 className="text-base font-semibold flex flex-wrap items-center gap-2 min-w-0">
            <span className="px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-xs">
              🧪 開発版(タブ)
            </span>
            病院実績ダッシュボード
            <span className="text-xs font-normal text-amber-700">
              リリース前タブ・社内レビュー用
            </span>
          </h1>
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {/* [2026-08-15] 区分ノート(診療報酬一次資料ビューア)へ。同一ホストの /kubun/ 配下
                (kubun-web・別コンテナ)なので相対パスで飛ぶ。dev 環境には無い=本番用導線 */}
            <a
              href="/kubun/"
              target="_blank"
              rel="noopener"
              className="text-xs text-blue-700 hover:underline"
              title="診療報酬 一次資料ビューア(告示・通知・疑義解釈・マスタを区分番号で横断)"
            >
              📖 区分ノート
            </a>
            {/* [2026-08-18] 点数本ミラーのリンクは正式版ヘッダ(App.tsx)へ移動した。
                β版に二重に置くと、どちらが正かが分からなくなるのでここには残さない。 */}
            <span className="text-slate-300 text-xs">|</span>
            <Link to="/" className="text-xs text-blue-700 hover:underline">
              ← 正式版へ戻る
            </Link>
            <span className="text-slate-300 text-xs">|</span>
            <AuthHeader />
          </div>
        </div>
      </header>

      <TabNav only={tabs} />

      <main
        className={[
          "w-full px-3 sm:px-4 py-3 grid gap-3 flex-1",
          isReferral
            ? sbOpen ? "grid-cols-[minmax(0,1fr)] md:grid-cols-[220px_minmax(0,1fr)]" : "grid-cols-[minmax(0,1fr)]"
            : isNursingAcuity
            ? "grid-cols-[minmax(0,1fr)]"
            : sbOpen
              ? "grid-cols-[180px_minmax(0,1fr)] md:grid-cols-[200px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)]"
              : "grid-cols-[minmax(0,1fr)]",
        ].join(" ")}
      >
        {!isNursingAcuity && sbOpen ? (
          isReferral ? <ReferralSidebar meta={meta} onCollapse={() => setSbOpen(false)} /> : <FilterBar meta={meta} onCollapse={() => setSbOpen(false)} />
        ) : !isNursingAcuity ? (
          <button
            onClick={() => setSbOpen(true)}
            className="no-print fixed left-1 top-24 z-30 card px-1.5 py-2 text-sm text-slate-600 hover:bg-slate-100 shadow"
            title="フィルタを開く"
          >
            ☰
          </button>
        ) : null}
        <div className="space-y-3 min-w-0">
          {!isNursingAcuity && !isReferral && (
            <ContextBar metrics={meta.metrics} builtAt={meta.builtAt} />
          )}
          {/* 入院実績(前年度・予算比較) は 2026-07-10 正式昇格 → App.tsx へ移動 */}
          {/* [2026-07-18] DPC分析・粗利分析(医師別なし) も正式昇格 → App.tsx へ移動。
              粗利分析(医師別あり) のみ開発版継続。 */}
          {/* [2026-08-03] 入院実績(医師別): 正式版「入院実績」と同じ構成 + 医師別マトリックス。
              正式版タブ (入院実績(前年度・予算比較)) は App.tsx 側でそのまま温存。 */}
          {f.tab === "入院実績(医師別)" && canViewDoctor && (
            <InpatientBudgetComparePanel
              metrics={meta.metrics}
              builtAt={meta.builtAt}
              dateMax={meta.dateMax}
              showDoctor
            />
          )}
          {f.tab === "外来収入" && <OutpatientRevenuePanel />}
          {f.tab === "紹介・入院分析" && <ReferralTrendsPanel />}
          {f.tab === "粗利分析" && <ProfitPanel />}
          {/* [2026-08-26 ユーザ指示] GP=延数×単価の分解ビュー (GP分析タブはそのまま温存) */}
          {f.tab === "粗利効率分析" && <GpEfficiencyPanel />}
          {/* [2026-07-21] 入院詳細実績/明細分析/元データ を正式版からβへ移動 */}
          {/* [2026-08-22] 入院KPI は廃止 (ユーザ指示「使わないので没」) */}
          {f.tab === "入院実績" && (
            <PrototypePanel metrics={meta.metrics} builtAt={meta.builtAt} />
          )}
          {f.tab === "明細分析" && <MeisaiPanel />}
          {f.tab === "元データ" && <DlPanel metrics={meta.metrics} />}
          {f.tab === "手術会計明細" && <SurgeryKaikeiPanel />}
          {f.tab === "請求会計速報" && <BillingReconPanel />}
          {f.tab === "速報売上" && <SokuhouUriagePanel />}
          {f.tab === "看護必要度速報" && <NursingAcuityPanel />}
          {f.tab === "データ辞書" && <DataDictPanel />}
          {f.tab === "参照データ一覧" && <ReferenceSourcesPanel />}
          {f.tab === "給与見込み・GP比較" && <SalaryForecastPanel />}
          {f.tab === "AI分析" && <AiAnalysisPanel />}
        </div>
      </main>

      <footer className="text-xs text-amber-600/70 text-center py-4">
        🧪 開発版(タブ) — 正式リリース前。フィードバックは経営企画部まで。
      </footer>
    </div>
  );
}
